import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Moon, Sun, ChevronRight } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import WhatsAppFab from "@/components/WhatsAppFab";
import { SITE } from "@/lib/site";

/**
 * PublicPageLayout — shared header/footer/nav for all marketing & legal pages.
 * Matches Landing.jsx styling exactly so branding is preserved.
 */
export default function PublicPageLayout({ children, breadcrumbs = [] }) {
  const { theme, toggle } = useTheme();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border glass-header sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" aria-label="Registry Feed home">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-heading font-bold text-lg">R</div>
            <div className="leading-tight">
              <div className="font-heading font-bold text-lg">Registry<span className="text-primary">Feed</span></div>
              <div className="text-[10px] tracking-widest text-muted-foreground uppercase">GST · MCA Data</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <Link to="/mca-database" className="px-3 py-1.5 rounded-md hover:bg-accent">MCA</Link>
            <Link to="/gst-database" className="px-3 py-1.5 rounded-md hover:bg-accent">GST</Link>
            <Link to="/new-company-database" className="px-3 py-1.5 rounded-md hover:bg-accent">New Companies</Link>
            <Link to="/pricing" className="px-3 py-1.5 rounded-md hover:bg-accent">Pricing</Link>
            <Link to="/faq" className="px-3 py-1.5 rounded-md hover:bg-accent">FAQ</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Link to="/login"><Button variant="ghost" size="sm">Log in</Button></Link>
            <Link to="/register"><Button size="sm" className="btn-shine">Get started</Button></Link>
          </div>
        </div>
      </header>

      {breadcrumbs.length > 0 && (
        <div className="border-b border-border bg-muted/30">
          <nav aria-label="Breadcrumb" className="mx-auto max-w-[1400px] px-4 sm:px-6 py-2.5 text-xs text-muted-foreground flex items-center flex-wrap gap-1">
            {breadcrumbs.map((b, i) => (
              <React.Fragment key={b.path}>
                {i > 0 && <ChevronRight className="h-3 w-3" />}
                {i < breadcrumbs.length - 1 ? (
                  <Link to={b.path} className="hover:text-foreground">{b.name}</Link>
                ) : (
                  <span className="text-foreground">{b.name}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
      )}

      <main>{children}</main>

      <footer className="border-t border-border bg-muted/20 mt-20">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-12 grid grid-cols-2 sm:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="font-heading font-bold mb-3">Products</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/mca-database" className="hover:text-foreground">MCA Company Database</Link></li>
              <li><Link to="/gst-database" className="hover:text-foreground">GST Company Database</Link></li>
              <li><Link to="/new-company-database" className="hover:text-foreground">New Company Data</Link></li>
              <li><Link to="/mca-gst-database" className="hover:text-foreground">MCA + GST Combo</Link></li>
              <li><Link to="/sample-data" className="hover:text-foreground">Sample Data</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold mb-3">Company</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/about" className="hover:text-foreground">About Us</Link></li>
              <li><Link to="/why-registryfeed" className="hover:text-foreground">Why Registry Feed</Link></li>
              <li><Link to="/how-it-works" className="hover:text-foreground">How It Works</Link></li>
              <li><Link to="/data-coverage" className="hover:text-foreground">Data Coverage</Link></li>
              <li><Link to="/faq" className="hover:text-foreground">FAQ</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold mb-3">Contact</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/contact" className="hover:text-foreground">Contact / Get Quote</Link></li>
              <li><Link to="/pricing" className="hover:text-foreground">Pricing</Link></li>
              <li><a href={`mailto:${SITE.salesEmail}`} className="hover:text-foreground">{SITE.salesEmail}</a></li>
              <li><span className="num">{SITE.whatsappDisplay}</span></li>
            </ul>
          </div>
          <div>
            <div className="font-heading font-bold mb-3">Legal</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-foreground">Terms &amp; Conditions</Link></li>
              <li><Link to="/refund-policy" className="hover:text-foreground">Refund Policy</Link></li>
              <li><Link to="/disclaimer" className="hover:text-foreground">Disclaimer</Link></li>
              <li><Link to="/acceptable-use" className="hover:text-foreground">Acceptable Use</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-foreground">Cookie Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-5">
            <div className="text-xs text-muted-foreground mb-3">
              <span className="font-medium text-foreground/70">Popular Searches</span>
              <div className="mt-2 flex flex-wrap gap-x-1 gap-y-1.5">
                {[
                  ["MCA Company Database", "/mca-database"],
                  ["GST Database India", "/gst-database"],
                  ["Newly Registered Company Data", "/new-company-database"],
                  ["Buy MCA Database", "/mca-database"],
                  ["Fresh Business Leads India", "/new-company-database"],
                  ["New GST Registration List", "/gst-database"],
                  ["Company Database India", "/"],
                  ["MCA Data Provider", "/mca-database"],
                  ["GST Data Provider India", "/gst-database"],
                  ["New Company Leads", "/new-company-database"],
                ].map(([label, to], i) => (
                  <React.Fragment key={label}>
                    {i > 0 && <span className="mx-1 text-border">|</span>}
                    <Link to={to} className="hover:text-foreground transition-colors">{label}</Link>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <div>© {new Date().getFullYear()} {SITE.brand}. Newly registered company data for B2B teams.</div>
              <div className="num">India · GST · MCA · Daily feed</div>
            </div>
          </div>
        </div>
      </footer>

      <WhatsAppFab />
    </div>
  );
}
